import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { db, storage } from "./firebase/firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Save, Plus, Trash, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "./context/UserContext";

// ==== Styled Components ====
const PageContainer = styled.div`
  background: #121212;
  color: #fff;
  min-height: 100vh;
  padding: 30px;
  font-family: "Roboto", sans-serif;
`;

const Card = styled.div`
  background: #1e1e2f;
  padding: 20px;
  border-radius: 12px;
  max-width: 900px;
  margin: auto;
  position: relative;
`;

const Title = styled.h2`
  color: #00d4ff;
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border: none;
  resize: vertical;
`;

const Label = styled.label`
  display: block;
  margin-top: 15px;
  font-weight: bold;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #00d4ff;
  color: #121212;
  border: none;
  padding: 12px 18px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background: #00bcd4;
  }
`;

const ChapterList = styled.div`
  margin-top: 15px;
  background: #272743;
  padding: 15px;
  border-radius: 8px;
`;

const ChapterItem = styled.div`
  display: flex;
  justify-content: space-between;
  background: #1e1e2f;
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 6px;
`;

// Loader Overlay
const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(18, 18, 18, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  z-index: 10;
`;

const EditCoursePage = () => {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState({
    title: "",
    description: "",
    price: "",
    discount: "",
    category: "",
    tutor: "",
    imageUrl: "",
    demoVideoUrl: "",
    image: null,
    demoVideo: null,
  });

  const [chapters, setChapters] = useState([]);
  const [newChapter, setNewChapter] = useState({
    title: "",
    duration: "",
    video: null,
  });

  const [loading, setLoading] = useState(false);

  // Fetch existing course
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const courseRef = doc(db, "courses", id);
        const snap = await getDoc(courseRef);
        if (snap.exists()) {
          const data = snap.data();
          setCourse({
            title: data.title || "",
            description: data.description || "",
            price: data.price || "",
            discount: data.discount || "",
            category: data.category || "",
            tutor: data.tutor || "",
            imageUrl: data.imageUrl || "",
            demoVideoUrl: data.demoVideoUrl || "",
            image: null,
            demoVideo: null,
          });
          setChapters(data.chapters || []);
        } else {
          alert("Course not found ❌");
          navigate("/admin-panel");
        }
      } catch (err) {
        console.error("Error fetching course:", err);
      }
    };
    fetchCourse();
  }, [id, navigate]);

  // Add Chapter
  const handleAddChapter = () => {
    if (!newChapter.title) {
      alert("Please enter chapter title");
      return;
    }
    setChapters([...chapters, newChapter]);
    setNewChapter({ title: "", duration: "", video: null });
  };

  const handleRemoveChapter = (index) => {
    setChapters(chapters.filter((_, i) => i !== index));
  };

  // Update Course
  const handleUpdate = async () => {
    try {
      if (!user?.id) {
        alert("User not logged in ❌");
        return;
      }
      setLoading(true);

      let imageUrl = course.imageUrl;
      let demoVideoUrl = course.demoVideoUrl;

      // Upload new Image if selected
      if (course.image) {
        const imgRef = ref(
          storage,
          `courses/images/${Date.now()}_${course.image.name}`
        );
        await uploadBytesResumable(imgRef, course.image);
        imageUrl = await getDownloadURL(imgRef);
      }

      // Upload new Demo Video if selected
      if (course.demoVideo) {
        const videoRef = ref(
          storage,
          `courses/demo/${Date.now()}_${course.demoVideo.name}`
        );
        await uploadBytesResumable(videoRef, course.demoVideo);
        demoVideoUrl = await getDownloadURL(videoRef);
      }

      // Upload new chapter videos
      const uploadedChapters = [];
      for (const chapter of chapters) {
        let videoUrl = chapter.videoUrl || "";
        if (chapter.video) {
          const chapRef = ref(
            storage,
            `courses/chapters/${Date.now()}_${chapter.video.name}`
          );
          await uploadBytesResumable(chapRef, chapter.video);
          videoUrl = await getDownloadURL(chapRef);
        }
        uploadedChapters.push({
          title: chapter.title,
          duration: chapter.duration,
          videoUrl,
        });
      }

      const courseRef = doc(db, "courses", id);

      await updateDoc(courseRef, {
        title: course.title,
        description: course.description,
        price: parseFloat(course.price),
        discount: parseFloat(course.discount) || 0,
        category: course.category,
        tutor: course.tutor,
        imageUrl,
        demoVideoUrl,
        chapters: uploadedChapters,
        updatedAt: new Date(),
        updatedBy: user?.id || null,
      });

      alert("Course Updated ✅");
      navigate("/admin-panel");
    } catch (err) {
      console.error(err);
      alert("Error updating course ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        {loading && (
          <LoaderOverlay>
            <Loader2 size={40} className="spin" />
          </LoaderOverlay>
        )}

        <Title>Edit Course</Title>

        {/* Course Info */}
        <Label>Course Title</Label>
        <Input
          type="text"
          value={course.title}
          onChange={(e) => setCourse({ ...course, title: e.target.value })}
        />

        <Label>Description</Label>
        <TextArea
          value={course.description}
          onChange={(e) =>
            setCourse({ ...course, description: e.target.value })
          }
        />

        <Label>Current Image</Label>
        {course.imageUrl && (
          <img
            src={course.imageUrl}
            alt="Course"
            style={{
              width: "150px",
              borderRadius: "8px",
              marginBottom: "10px",
            }}
          />
        )}
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => setCourse({ ...course, image: e.target.files[0] })}
        />

        <Label>Current Demo Video</Label>
        {course.demoVideoUrl && (
          <video
            src={course.demoVideoUrl}
            width="250"
            controls
            style={{ marginBottom: "10px", borderRadius: "8px" }}
          />
        )}
        <Input
          type="file"
          accept="video/*"
          onChange={(e) =>
            setCourse({ ...course, demoVideo: e.target.files[0] })
          }
        />

        <Label>Price (₹)</Label>
        <Input
          type="number"
          value={course.price}
          onChange={(e) => setCourse({ ...course, price: e.target.value })}
        />

        <Label>Offer Discount (%)</Label>
        <Input
          type="number"
          value={course.discount}
          onChange={(e) => setCourse({ ...course, discount: e.target.value })}
        />

        <Label>Category</Label>
        <Input
          type="text"
          value={course.category}
          onChange={(e) => setCourse({ ...course, category: e.target.value })}
        />

        <Label>Tutor</Label>
        <Input
          type="text"
          value={course.tutor}
          onChange={(e) => setCourse({ ...course, tutor: e.target.value })}
        />

        {/* Chapters */}
        <Title style={{ marginTop: "30px" }}>Chapters</Title>
        <Label>Chapter Title</Label>
        <Input
          type="text"
          value={newChapter.title}
          onChange={(e) =>
            setNewChapter({ ...newChapter, title: e.target.value })
          }
        />

        <Label>Duration</Label>
        <Input
          type="text"
          placeholder="e.g. 15min"
          value={newChapter.duration}
          onChange={(e) =>
            setNewChapter({ ...newChapter, duration: e.target.value })
          }
        />

        <Label>Upload Video</Label>
        <Input
          type="file"
          accept="video/*"
          onChange={(e) =>
            setNewChapter({ ...newChapter, video: e.target.files[0] })
          }
        />

        <Button type="button" onClick={handleAddChapter}>
          <Plus size={18} /> Add Chapter
        </Button>

        {/* List of Chapters */}
        <ChapterList>
          {chapters.map((chap, index) => (
            <ChapterItem key={index}>
              <span>
                {chap.title} ({chap.duration})
              </span>
              <Trash
                size={18}
                style={{ cursor: "pointer", color: "red" }}
                onClick={() => handleRemoveChapter(index)}
              />
            </ChapterItem>
          ))}
        </ChapterList>

        {/* Submit */}
        <Button onClick={handleUpdate} disabled={loading}>
          {loading ? (
            "Updating..."
          ) : (
            <>
              <Save size={18} /> Update Course
            </>
          )}
        </Button>
      </Card>
    </PageContainer>
  );
};

export default EditCoursePage;
